import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Alert } from "components/elements";
import ModalLevel from "components/elements/Modal/ModalLevel";

import { generateRandomString } from "utils/helper";
import * as services from "../services/organizationApi";
import {
  setCompanyInformation,
  setDepartment,
  setDepartmentListOption,
  setIsLoading,
  setOrganizationLevelListOption,
} from "../slices/companySlice";
import { Modal } from "antd";

function simplifyArrayToNested(array) {
  const map = new Map();
  const result = [];

  for (const obj of array) {
    obj.children = [];
    map?.set(obj?.organizationId, obj);
  }

  for (const obj of array) {
    if ((obj?.under?.id || obj?.under?.id === "") && map?.has(obj?.under?.id)) {
      const parent = map?.get(obj?.under?.id);
      parent?.children?.push(obj);
    } else {
      result.push(obj);
    }
  }
  return result;
}

function useOrganization(props = {}) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.company.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const departmentList = useSelector((state) => state.company.departmentList);
  const departmentListOption = useSelector(
    (state) => state.company.departmentListOption
  );
  const companyInformation = useSelector(
    (state) => state.company.companyInformation
  );
  const [levelModal, setLevelModal] = useState(false);
  const [createOrgnizationModal, setCreateOrgnizationModal] = useState(false);
  const [editOrgnizationModal, setEditOrgnizationModal] = useState(false);
  const organizationLevelOption = useSelector(
    (state) => state.company.organizationLevelList
  );
  const currentCompanyProfile = useSelector((state) => state.company.currentCompanyProfile);

  const [lang, setLang] = useState("en");

  const modifiedOrganizationLevelOptions = useMemo(() => {
    const modifiedOptions = organizationLevelOption?.map((opt) => ({
      ...opt,
      organizationLevelName: opt?.organizationLevelName,
      organizationLevelNameEn: opt?.organizationLevelNameEn,
      value: opt?.level,
      label:
        lang === "en" ? opt?.organizationLevelNameEn : opt?.organizationLevelName,
    }));

    return modifiedOptions;
  }, [lang, organizationLevelOption]);

  const modifiedOrganizationUnderOptions = useMemo(() => {
    const modifiedOptions = departmentListOption?.map((opt) => ({
      ...opt,
      value: opt?.organizationId,
      label: lang === "en" ? opt?.organizationNameEn : opt?.organizationName,
    }));

    return modifiedOptions;
  }, [lang, departmentListOption]);

  const handleOpenLevelModal = (value) => {
    setLevelModal(value);
  };

  const onCancel = () => {
    setLevelModal(false);
  };

  const onSubmitOrganizationLevel = (values) => {
    let working = false;
    values.organization?.map(async (e) => {
      if (e?.organizationLevelId != null) {
        let oldData = modifiedOrganizationLevelOptions.find(
          (elem) => elem?.organizationLevelId === e?.organizationLevelId
        );
        if (
          oldData?.organizationLevelName !== e?.organizationLevelName ||
          oldData?.organizationLevelNameEn !== e?.organizationLevelNameEn
        ) {
          working = true;
          await updateOrganizationLevel(e);
        }
      } else {
        working = true;
        await createOrganizationLevel(e);
      }
    });

    modifiedOrganizationLevelOptions?.map(async (e) => {
      let newData = values?.organization?.find(
        (elem) => elem?.organizationLevelId === e?.organizationLevelId
      );
      if (newData == null) {
        working = true;
        await deleteOrganizationLevel(e);
      }
    });

    if (!working) setLevelModal(false);
  };

  const getOrganization = useCallback(() => {
    return (
      <ModalLevel
        organizationLevelOption={modifiedOrganizationLevelOptions}
        title={t("organization_level.header")}
        isModalOpen={levelModal}
        handleOk={onCancel}
        handleCancel={onCancel}
        onSubmit={onSubmitOrganizationLevel}
      />
    );
  }, [levelModal, modifiedOrganizationLevelOptions]);

  const getDepartment = useCallback(async () => {
    if (!currentCompany) return null;
    try {
      dispatch(setIsLoading(true));
      const response = await services.getDepartment({
        requestId: generateRandomString(),
        companyId: currentCompany,
      });
      dispatch(setCompanyInformation(response?.data?.companyInformation || currentCompanyProfile));
      dispatch(
        setDepartment(simplifyArrayToNested(response?.data?.organizationList || []))
      );
      dispatch(setDepartmentListOption(response?.data?.organizationList));
    } catch (error) {
      Alert({ type: "error", resultObject: error });
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, dispatch]);

  const createDepartment = useCallback(
    async (record, cb = () => { }) => {
      try {
        dispatch(setIsLoading(true));
        console.log("record", record);

        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          departmentCode: record.agencyCode,
          organizationName: record.organizationLevelNameTH,
          organizationNameEn: record.organizationLevelNameEN,
          organizationLevelId: modifiedOrganizationLevelOptions.find(
            (e) => parseInt(e.level) === parseInt(record.level) + 1
          ).organizationLevelId,
          // organizationLevelId: record.organizationId,
          parentId: record.under,
          level: record.level.toString(),
          socialSecurityBranch: record.socialSecurityBranchOptions,
          accountInformation: record.information ? "Y" : "N", // Y / N
          accountTypeCodes: record.accountTypeCodes,
          costCenter: record.costCenter,
          businessUnit: record.businessUnit,
        };
        const response = await services.createDepartment(values);
        setCreateOrgnizationModal(false);
        getDepartment();
        cb();

        if (response?.data?.content?.code === "400") {
          Modal.error({
            content: response?.data?.content?.details?.[0]?.key === "departmentCode" ? t("organization.label.warning_department") : response?.data?.content?.details?.[0]?.value,
            okText: t("message.ok"),
          });
          return;
        }
      }
      catch (error) {
        if (error instanceof TypeError) {
          Alert({
            type: "error",
            message: "ไม่สามารถสร้างได้เนื่องจากถึงลำดับขั้นสุดท้ายแล้ว",
            resultObject: "",
          });
        } else Alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, modifiedOrganizationLevelOptions, dispatch]
  );

  const getOrganizationLevelList = useCallback(
    async (record) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getOrganizationLevelList({
          requestId: generateRandomString(),
          companyId: currentCompany,
        });

        dispatch(setOrganizationLevelListOption(response.data));
      } catch (error) {
        Alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const deleteDepartment = useCallback(
    async (organizationId) => {
      try {
        dispatch(setIsLoading(true));
        const values =
        // await services.deleteDepartment(
        {
          requestId: generateRandomString(),
          companyId: currentCompany,
          organizationId: organizationId,
          status: "DELETE",
        };
        // );
        const response = await services.deleteDepartment(values)

        if (response?.data?.content?.code === "400") {
          Modal.error({
            content: response?.data?.content?.details?.[0]?.value,
            okText: t("message.ok"),
          });
          return;
        }
      } catch (error) {
        if (error?.response?.status === 403) {
          Modal.error({
            content:
              i18n.language === "en"
                ? "You do not have permission to perform this action."
                : "คุณไม่มีสิทธิ์ในการดำเนินการนี้",
            okText: t("message.ok"),
          });
        } else {
          Alert({ type: "error", resultObject: error });
        }
      } finally {
        dispatch(setIsLoading(false));
        await getDepartment();
      }
    },
    [currentCompany, dispatch, getDepartment, t]
  );

  const updateDepartment = useCallback(
    async (record) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          departmentCode: record.agencyCode,
          organizationName: record.organizationLevelNameTH,
          organizationNameEn: record.organizationLevelNameEN,
          organizationLevelId: record.node.organizationLevel.id,
          parentId: record.under,
          departmentId: record.agencyCode,
          level: record.node.level.toString(),
          socialSecurityBranch: record.socialSecurityBranchOptions,
          accountInformation: record.information ? "Y" : "N", // Y / N
          accountTypeCodes: record.accountTypeCodes,
          costCenter: record.costCenter,
          businessUnit: record.businessUnit,
          organizationId: record.node.organizationId,
        };
        await services.updateDepartment(values);
        setEditOrgnizationModal(false);
        await getDepartment();
      } catch (error) {
        Alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const createOrganizationLevel = useCallback(
    async (record) => {
      try {
        dispatch(setIsLoading(true));
        await services.createOrganizationLevel({
          requestId: generateRandomString(),
          companyId: currentCompany,
          organizationLevelName: record.organizationLevelName,
          organizationLevelNameEn: record.organizationLevelNameEn,
          level: record.level,
        });
        await getOrganizationLevelList();
        setLevelModal(false);
      } catch (error) {
        Alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const updateOrganizationLevel = useCallback(
    async (record) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          organizationLevelName: record.organizationLevelName.toString(),
          organizationLevelNameEn: record.organizationLevelNameEn.toString(),
          organizationLevelId: record.organizationLevelId,
          level: record.level,
        };
        await services.updateOrganizationLevel(values);
        await getOrganizationLevelList();
        setLevelModal(false);
      } catch (error) {
        Alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const deleteOrganizationLevel = useCallback(
    async (record) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          organizationLevelId: record.organizationLevelId,
          level: record.level,
        };
        await services.deleteOrganizationLevel(values).then(o => {
          console.log("then", o
          );
        }).catch(error => {

          console.log("catch", error);
          console.error("API Error:", error);

          if (error?.response?.status === 403) {
            Modal.error({
              content: i18n.language == "en" ? "You do not have permission to perform this action." : "คุณไม่มีสิทธิ์ในการดำเนินการนี้",
              okText: t("message.ok"),
            });
          } else if (error?.data?.content?.code === "400") {
            Modal.error({
              content: error?.data?.content?.details[0]?.value,
              okText: t("message.ok"),
            });
          } else {
            setLevelModal(false);
          }
        });
      } catch (error) {
        // Alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
        await getOrganizationLevelList();
      }
    },
    [currentCompany, dispatch]
  );

  useEffect(() => {
    setLang(i18n.language);
  }, [i18n.language, setLang]);

  useEffect(() => {
    if (currentCompany != null) {
      getOrganizationLevelList();
      getDepartment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);

  return {
    getDepartment,
    getOrganizationLevelList,
    handleOpenLevelModal,
    getOrganization,
    deleteDepartment,
    updateDepartment,
    onCancel,
    setCreateOrgnizationModal,
    setEditOrgnizationModal,
    createOrganizationLevel,
    updateOrganizationLevel,
    createDepartment, simplifyArrayToNested,
    onEdit: props.onEdit,
    lang,
    isLoading,
    organizationLevelOption: modifiedOrganizationLevelOptions,
    organizationUnderOptions: modifiedOrganizationUnderOptions,
    departmentList,
    departmentListOption,
    companyInformation,
    createOrgnizationModal,
    editOrgnizationModal,
  };
}

export default useOrganization;
